/**
 * Member-facing ETA brackets (pure).
 *
 * Owner decision (2026-10-08 field test): the member must never be shown an
 * exact minute count — the model under-estimated a 11.7-minute drive as
 * 5.6 min, the figure jumped "5 → 10 → 9" between fixes, and "Under a minute
 * away" sat on screen for 3.5 minutes. Exact numbers over-promise accuracy
 * the data does not have. Instead the member sees a 5-minute BRACKET:
 *
 *   "More than 30 min away" · "25–30 min away" · … · "5–10 min away" ·
 *   "Less than 5 min away" · "Arriving now" (arrival geofence) · "Arrived".
 *
 * The band only changes when the evidence clearly supports it (hysteresis —
 * see {@link advanceEtaBracket}), so it never flickers between two bands on
 * noisy estimates. Provider-side surfaces may keep exact numbers; this module
 * is for what the MEMBER reads and for the member pushes.
 */

export type EtaBracketId =
  | 'over_30'
  | '25_30'
  | '20_25'
  | '15_20'
  | '10_15'
  | '5_10'
  | 'under_5'
  | 'arriving'
  | 'arrived'

/** Width of each minute bracket. */
export const ETA_BRACKET_STEP_MINUTES = 5

/** Estimates at/above this are a single open-ended "more than" bracket. */
export const ETA_BRACKET_OPEN_ENDED_MINUTES = 30

/**
 * Minute brackets, closest first. `lower` is inclusive, `upper` exclusive;
 * `upper === null` is the open-ended top bracket.
 */
export const ETA_MINUTE_BRACKETS: ReadonlyArray<{
  id: EtaBracketId
  lower: number
  upper: number | null
}> = [
  { id: 'under_5', lower: 0, upper: 5 },
  { id: '5_10', lower: 5, upper: 10 },
  { id: '10_15', lower: 10, upper: 15 },
  { id: '15_20', lower: 15, upper: 20 },
  { id: '20_25', lower: 20, upper: 25 },
  { id: '25_30', lower: 25, upper: 30 },
  { id: 'over_30', lower: 30, upper: null },
]

/**
 * Ordinal used for "closer / further" comparisons. Lower = closer.
 * `arrived` (0) < `arriving` (1) < `under_5` (2) < … < `over_30` (8).
 */
export function etaBracketRank(id: EtaBracketId): number {
  if (id === 'arrived') return 0
  if (id === 'arriving') return 1
  return 2 + ETA_MINUTE_BRACKETS.findIndex((b) => b.id === id)
}

/** Raw bracket for an estimate in minutes (no hysteresis). */
export function etaBracketForMinutes(estimateMinutes: number): EtaBracketId {
  const minutes = Math.max(0, estimateMinutes)
  for (const bracket of ETA_MINUTE_BRACKETS) {
    if (bracket.upper == null || minutes < bracket.upper) return bracket.id
  }
  return 'over_30'
}

/**
 * Move DOWN (closer) only when the estimate is clearly inside the new band:
 * at least this far below the band's upper edge (minutes).
 */
export const ETA_BRACKET_DOWN_MARGIN_MINUTES = 1

/**
 * Without the clear-margin, a closer band is adopted after this many
 * consecutive estimates (from DIFFERENT GPS anchors) land in it.
 */
export const ETA_BRACKET_DOWN_CONSECUTIVE = 2

/**
 * Move UP (further away — traffic) only after this many consecutive estimates
 * from different anchors sit above the current band …
 */
export const ETA_BRACKET_UP_CONSECUTIVE = 2

/** … or after the estimate has stayed above the band for this long (ms). */
export const ETA_BRACKET_UP_DWELL_MS = 2 * 60_000

export interface EtaBracketSample {
  /**
   * Estimated minutes remaining (may be fractional). `null` when unknown —
   * the displayed band is held.
   */
  estimateMinutes: number | null
  /** Provider's latest real fix is inside the tight (150 m) arrival geofence. */
  withinArrivalGeofence?: boolean
  /** Journey arrived / provider checked in — terminal. */
  arrived?: boolean
  /**
   * Timestamp of the GPS fix the estimate is anchored to. Consecutive-sample
   * counting only advances when this changes, so a 5-second UI tick
   * re-deriving the same stale fix cannot count as "two estimates".
   */
  anchorTimestamp?: number | null
  now: number
}

export interface EtaBracketState {
  /** Bracket currently shown to the member (`null` before the first sample). */
  bracket: EtaBracketId | null
  /** When {@link bracket} was adopted. */
  since: number
  /** Candidate bracket from recent samples that disagree with {@link bracket}. */
  pending: EtaBracketId | null
  /** Number of distinct-anchor samples supporting {@link pending}. */
  pendingCount: number
  /** When the first supporting sample for {@link pending} arrived. */
  pendingSince: number
  /** Anchor of the last sample counted toward {@link pendingCount}. */
  lastCountedAnchor: number | null
}

export function initialEtaBracketState(): EtaBracketState {
  return {
    bracket: null,
    since: 0,
    pending: null,
    pendingCount: 0,
    pendingSince: 0,
    lastCountedAnchor: null,
  }
}

function adopt(bracket: EtaBracketId, now: number): EtaBracketState {
  return {
    bracket,
    since: now,
    pending: null,
    pendingCount: 0,
    pendingSince: 0,
    lastCountedAnchor: null,
  }
}

function clearPending(state: EtaBracketState): EtaBracketState {
  if (state.pending == null && state.pendingCount === 0) return state
  return { ...state, pending: null, pendingCount: 0, pendingSince: 0, lastCountedAnchor: null }
}

function upperEdgeMinutes(id: EtaBracketId): number | null {
  const bracket = ETA_MINUTE_BRACKETS.find((b) => b.id === id)
  return bracket ? bracket.upper : null
}

/**
 * Feed one estimate into the bracket state machine and get the next state.
 *
 * Rules (owner decision 2026-10-08):
 *  - `arrived` wins over everything and is terminal.
 *  - Inside the arrival geofence → "Arriving now" immediately (a geofence hit
 *    is a hard GPS fact, not an estimate). It is held while the fix stays in
 *    the geofence; if the provider drives back out, the minute brackets take
 *    over again under the normal move-UP rule.
 *  - First estimate → adopt its raw bracket.
 *  - Same bracket → no change.
 *  - Move DOWN (closer): immediately when the estimate is ≥
 *    {@link ETA_BRACKET_DOWN_MARGIN_MINUTES} below the new bracket's upper
 *    edge, otherwise after {@link ETA_BRACKET_DOWN_CONSECUTIVE} samples from
 *    different anchors agree.
 *  - Move UP (further): after {@link ETA_BRACKET_UP_CONSECUTIVE} samples from
 *    different anchors agree, or once the estimate has stayed above the
 *    current bracket for {@link ETA_BRACKET_UP_DWELL_MS}.
 *  - A sample that disagrees with the pending candidate restarts the count,
 *    so no bracket can flicker.
 */
export function advanceEtaBracket(
  state: EtaBracketState,
  sample: EtaBracketSample,
): EtaBracketState {
  const { now } = sample

  if (sample.arrived) {
    return state.bracket === 'arrived' ? state : adopt('arrived', now)
  }
  if (state.bracket === 'arrived') return state

  if (sample.withinArrivalGeofence) {
    return state.bracket === 'arriving' ? clearPending(state) : adopt('arriving', now)
  }

  if (sample.estimateMinutes == null || !Number.isFinite(sample.estimateMinutes)) {
    return state
  }

  const raw = etaBracketForMinutes(sample.estimateMinutes)
  if (state.bracket == null) return adopt(raw, now)
  if (raw === state.bracket) return clearPending(state)

  const anchor = sample.anchorTimestamp ?? null
  const movingCloser = etaBracketRank(raw) < etaBracketRank(state.bracket)

  // "Arriving" is only left by driving back out of the geofence; from there
  // the raw minute bracket is further away, so it follows the move-UP rule.

  if (movingCloser) {
    const upper = upperEdgeMinutes(raw)
    const clearlyInside =
      upper != null && sample.estimateMinutes <= upper - ETA_BRACKET_DOWN_MARGIN_MINUTES
    if (clearlyInside) return adopt(raw, now)
  }

  // Pending-candidate bookkeeping. A sample supports the pending candidate
  // when it lands in the same bracket OR (for a move in the same direction)
  // in an even more extreme bracket — a run of "further than current" readings
  // should not reset just because traffic estimates wobble between 20–25 and
  // 25–30.
  const sameDirection =
    state.pending != null &&
    (etaBracketRank(state.pending) < etaBracketRank(state.bracket)) === movingCloser
  const supportsPending = state.pending != null && sameDirection
  const isNewAnchor = anchor == null || anchor !== state.lastCountedAnchor

  let next: EtaBracketState
  if (!supportsPending) {
    next = {
      ...state,
      pending: raw,
      pendingCount: 1,
      pendingSince: now,
      lastCountedAnchor: anchor,
    }
  } else if (isNewAnchor) {
    next = {
      ...state,
      // Track the most recent reading's bracket so the adopted band is the
      // one the latest evidence points at.
      pending: raw,
      pendingCount: state.pendingCount + 1,
      lastCountedAnchor: anchor,
    }
  } else {
    next = { ...state, pending: raw }
  }

  const dwellMs = now - next.pendingSince
  if (movingCloser) {
    if (next.pendingCount >= ETA_BRACKET_DOWN_CONSECUTIVE) return adopt(next.pending!, now)
    return next
  }
  if (next.pendingCount >= ETA_BRACKET_UP_CONSECUTIVE || dwellMs >= ETA_BRACKET_UP_DWELL_MS) {
    return adopt(next.pending!, now)
  }
  return next
}

/* ------------------------------- wording ---------------------------------- */

/** En dash range, e.g. "15–20". */
function rangeText(lower: number, upper: number): string {
  return `${lower}\u2013${upper}`
}

/**
 * Lower-case phrase that completes "<Name> is …": "more than 30 min away",
 * "15–20 min away", "less than 5 min away", "arriving now". `arrived` has no
 * "is" phrase — use {@link formatEtaBracketSentence}.
 */
export function etaBracketPhrase(id: EtaBracketId): string {
  if (id === 'arrived') return 'here'
  if (id === 'arriving') return 'arriving now'
  if (id === 'over_30') return `more than ${ETA_BRACKET_OPEN_ENDED_MINUTES} min away`
  if (id === 'under_5') return `less than ${ETA_BRACKET_STEP_MINUTES} min away`
  const bracket = ETA_MINUTE_BRACKETS.find((b) => b.id === id)
  if (!bracket || bracket.upper == null) return 'on the way'
  return `${rangeText(bracket.lower, bracket.upper)} min away`
}

/** Capitalised standalone label: "15–20 min away", "Arriving now", "Arrived". */
export function etaBracketLabel(id: EtaBracketId): string {
  if (id === 'arrived') return 'Arrived'
  const phrase = etaBracketPhrase(id)
  return phrase.charAt(0).toUpperCase() + phrase.slice(1)
}

/**
 * Full second-person-friendly sentence using the provider's FIRST name:
 * "Zach is 15–20 min away." · "Zach is arriving now." · "Zach has arrived."
 */
export function formatEtaBracketSentence(id: EtaBracketId, providerFirstName: string): string {
  const name = providerFirstName.trim() || 'Your provider'
  if (id === 'arrived') return `${name} has arrived.`
  return `${name} is ${etaBracketPhrase(id)}.`
}

/**
 * Member push copy for the three journey pushes that remain (owner decision
 * 2026-10-08 — no "halfway" / "nearly there" intermediates):
 *  - started:  "Zach is on the way — about 20–25 min away"
 *  - close:    "Zach is less than 5 min away"
 *  - arrived:  "Zach has arrived"
 * First names only, no addresses (push privacy).
 */
export function formatJourneyStartedPushBody(
  providerFirstName: string,
  bracket: EtaBracketId | null,
): string {
  const name = providerFirstName.trim() || 'Your provider'
  if (bracket == null || bracket === 'arrived' || bracket === 'arriving') {
    return `${name} is on the way.`
  }
  const phrase = etaBracketPhrase(bracket)
  const about = bracket === 'over_30' || bracket === 'under_5' ? phrase : `about ${phrase}`
  return `${name} is on the way \u2014 ${about}.`
}
